import React, { useEffect } from 'react';
import AppLogo from '@/core/presentation/components/ui/AppLogo';
import { BRAND_LOGO_SRC } from '@/core/presentation/constants/brand-assets';
import { LogoLink } from '@/features/app/presentation/components/LogoLink';
import {
  AUTH_LAYOUT_HTML_CLASS,
  AUTH_LAYOUT_LOGO_RESPONSIVE_CLASS,
} from '@/features/authentication/presentation/composables/constants/auth-layout.constants';

export interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Rodapé opcional (links, texto legal, CTAs secundários). Fica no fluxo da coluna, abaixo do card. */
  footer?: React.ReactNode;
  /** Largura máxima do card (Tailwind). Padrão: `max-w-md`. */
  maxWidthClassName?: string;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  footer,
  maxWidthClassName = 'max-w-md',
}) => {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add(AUTH_LAYOUT_HTML_CLASS);
    return () => {
      html.classList.remove(AUTH_LAYOUT_HTML_CLASS);
    };
  }, []);

  return (
    <>
      {/*
        PWA (viewport-fit=cover + black-translucent): uma única camada full-viewport com
        background-color + mesmo gradiente que `.static-gradient-bg`, alinhada a html/body/#root
        via classe `auth-layout-active` em tailwind.css — sem costuras nas safe areas.
      */}
      <div className="auth-layout-viewport-fill" aria-hidden />

      <div
        className="pointer-events-none fixed left-1/2 top-1/2 z-[1] h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2"
        style={{
          background: 'radial-gradient(circle, rgba(126,207,239,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
        aria-hidden
      />

      {/*
        Centro na viewport inteira (sem header). Padrão min-h-full + flex justify-center:
        bloco centrado quando cabe; com conteúdo alto, rola com padding vertical.
      */}
      <div className="fixed inset-0 z-10 overflow-x-hidden overflow-y-auto overscroll-y-none">
        <main
          className={`font-app antialiased mx-auto flex min-h-full w-full flex-col items-center justify-center gap-3 px-4 py-8 sm:gap-3 sm:px-6 sm:py-10 ${maxWidthClassName}`}
        >
          <div className="flex shrink-0 justify-center">
            <LogoLink
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark rounded-lg overflow-visible shrink-0 block"
              ariaLabel="RetornaAr - Voltar ao início"
            >
              <AppLogo src={BRAND_LOGO_SRC} responsiveClassName={AUTH_LAYOUT_LOGO_RESPONSIVE_CLASS} />
            </LogoLink>
          </div>

          <div className="w-full min-w-0">
            <div className="w-full glass-panel rounded-2xl p-6 shadow-xl sm:p-8">
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-semibold text-white sm:text-2xl">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">{subtitle}</p>
                ) : null}
              </div>
              {children}
            </div>
          </div>

          {footer ? (
            <div className="flex w-full shrink-0 justify-center pt-1 sm:pt-0">{footer}</div>
          ) : null}
        </main>
      </div>
    </>
  );
};
